import {
  Box,
  Flex,
  List,
  ListIcon,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
} from '@chakra-ui/react';
import { ReactComponent as CheckCircleIcon } from 'assets/icons/check_circle.svg';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { LoadingButton } from 'modules/common/components/Button/LoadingButton';
import { useTranslation } from 'react-i18next';
import { MemberPlans } from 'types';

type InviteTeamMemberModalProps = {
  isOpen: boolean;
  isLoading?: boolean;
  seats: number;
  loadingText?: string;
  onClose?: () => void;
  onClick?: () => void;
};

type InviteTeamMemberModalContentProps = {
  price: string;
  memberPlanInterval: MemberPlans;
  memberDiscountedFullPrice: string;
  isLoading: boolean;
  loadingText: string;
  seats: number;
  onClick: () => void;
};

const DEFAULT_PRICE = '$49';
const DEFAULT_PERIOD = 'monthly';

const InviteTeamMemberModalContent = ({
  price,
  memberPlanInterval,
  memberDiscountedFullPrice,
  isLoading,
  loadingText,
  seats,
  onClick,
}: InviteTeamMemberModalContentProps) => {
  const { t } = useTranslation('common_navbar');

  const modalBodyTestId = 'invite-team-member-modal-body';
  const modalCloseButtonTestId = 'invite-team-member-modal-close-button';
  const modalActionButtonTestId = 'invite-team-member-modal-action-button';

  return (
    <ModalContent>
      <ModalHeader color='font.500'>{t('modals.share-panel.invite-team-members.modal.title')}</ModalHeader>
      <ModalCloseButton data-testid={modalCloseButtonTestId} />
      <ModalBody data-testid={modalBodyTestId}>
        <Flex flexDir='column' justifyContent='space-between' gap={4} pb={4} color='font.500'>
          <Flex>
            {/* If user has paid seats available, show that, if not. Show the price  */}
            {seats > 0 ? (
              <Text>
                {seats} {t('modals.share-panel.invite-team-members.modal.seats-remaining')} &nbsp;
              </Text>
            ) : (
              <>
                <Text>{t('modals.share-panel.invite-team-members.modal.description1')} &nbsp;</Text>
                <Text as='b'>{t('modals.share-panel.invite-team-members.modal.description2', { price })}</Text>
              </>
            )}
          </Flex>
          <Text as='b'>{t('modals.share-panel.invite-team-members.modal.sub-title')}</Text>
          <Box bg='upgrade.blue.50' borderRadius={6} p={4} fontSize='sm'>
            <List spacing={2}>
              <ListItem display='flex' alignItems='center'>
                <ListIcon as={CheckCircleIcon} color='green.500' />
                {t('modals.share-panel.invite-team-members.modal.sub-item1')}
              </ListItem>
              <ListItem display='flex' alignItems='center'>
                <ListIcon as={CheckCircleIcon} color='green.500' />
                {t('modals.share-panel.invite-team-members.modal.sub-item2')}
              </ListItem>
              <ListItem display='flex' alignItems='center'>
                <ListIcon as={CheckCircleIcon} color='green.500' />
                {t('modals.share-panel.invite-team-members.modal.sub-item3')}
              </ListItem>
            </List>
          </Box>
          <Text fontSize='sm'>
            {t('modals.share-panel.invite-team-members.modal.sub-description', {
              price: `${memberDiscountedFullPrice}/${t(
                `modals.share-panel.invite-team-members.modal.${memberPlanInterval}`,
              )}`,
            })}
          </Text>
          <Flex w='full'>
            <Spacer />
            <LoadingButton
              variant='upgrade'
              size='sm'
              onClick={onClick}
              isLoading={isLoading}
              loadingText={loadingText}
              data-testid={modalActionButtonTestId}
            >
              {t('modals.share-panel.invite-team-members.modal.invite-button')}
            </LoadingButton>
          </Flex>
        </Flex>
      </ModalBody>
    </ModalContent>
  );
};

export const InviteTeamMemberModal = ({
  isOpen,
  isLoading = false,
  loadingText = '',
  seats,
  onClose = () => {},
  onClick = () => {},
}: InviteTeamMemberModalProps) => {
  const { venngageE2 } = useVenngageE1Gate();
  const memberDiscountedPrice = venngageE2?.memberDiscountedPrice ?? DEFAULT_PRICE;
  const memberDiscountedFullPrice = venngageE2?.memberDiscountedFullPrice ?? DEFAULT_PRICE;
  const memberPlanInterval = venngageE2?.memberPlanInterval ?? DEFAULT_PERIOD;

  return (
    <div>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size='xl'>
        <ModalOverlay />
        <InviteTeamMemberModalContent
          price={memberDiscountedPrice}
          memberPlanInterval={memberPlanInterval}
          memberDiscountedFullPrice={memberDiscountedFullPrice}
          isLoading={isLoading}
          seats={seats}
          loadingText={loadingText}
          onClick={onClick}
        />
      </Modal>
    </div>
  );
};
